import { basicFormatResult } from "@onesolution/serverFn/basicFormatter";
import lodash from "lodash";
import { mondayClient } from "./monday";

const defaultMerge = (results: Array<unknown>) => {
	return results.reduce(
		(out: { items: Array<unknown> }, obj) => {
			out.items?.push(...((obj as { items: Array<unknown> }).items || []));

			return out;
		},
		{ items: [] },
	);
};
type AutobatchParams = {
	key?: string;
	chunkSize?: number;
	mergeWith?: typeof defaultMerge;
};

async function _autobatchRequest(
	query: string,
	params: object,
	opts: AutobatchParams,
) {
	const { key = "itemIds", chunkSize = 20, mergeWith = defaultMerge } = opts;
	const array = lodash.get(params, key) as Array<unknown>;
	const chunks = lodash.chunk(array, chunkSize);

	const results = await Promise.all(
		chunks.map((chunk) => {
			const paramsWithLimit = lodash.clone(params);
			lodash.set(paramsWithLimit, key, chunk);

			return mondayClient.request(query, paramsWithLimit);
		}),
	);

	return mergeWith(results as Array<unknown>);
}

type AdditionalFormatting<T> = (
	rawData: unknown,
	formattedData: unknown,
) => Promise<T>;

type QueryConfig<T> = {
	gql: string;
	resultKey: string;
	defaultParams: object;
	outType: T;
	additionalFormatting?: AdditionalFormatting<T> | AdditionalFormatting<T>[];
	autobatch?: AutobatchParams;
	skipMondayFormatting?: boolean;
};

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export type AnyFn = (...args: any) => any;

export async function requestAndFormat<
	S extends QueryConfig<S["outType"]>,
	T extends AnyFn,
>(queryConfig: S, params?: Parameters<T>[0]): Promise<S["outType"]> {
	const {
		gql,
		defaultParams,
		outType,
		resultKey,
		autobatch,
		additionalFormatting,
		skipMondayFormatting = false,
	} = queryConfig;
	let rep: unknown;
	const formattedParams = { ...defaultParams, ...params };
	if (autobatch) {
		rep = await _autobatchRequest(gql, formattedParams, autobatch);
	} else {
		rep = await mondayClient.request(gql, formattedParams);
	}

	const items = lodash.get(rep, resultKey);

	if (skipMondayFormatting) return items as typeof outType;

	const mondayFormatted = skipMondayFormatting
		? items
		: basicFormatResult(Array.isArray(items) ? items : [items]);
	const typeFormatted =
		skipMondayFormatting || Array.isArray(outType) || additionalFormatting
			? mondayFormatted
			: mondayFormatted[0];

	let fullyFormatted = typeFormatted as S["outType"];
	if (Array.isArray(additionalFormatting)) {
		for (const formatter of additionalFormatting) {
			fullyFormatted = await formatter(rep, fullyFormatted);
		}
	} else if (additionalFormatting) {
		fullyFormatted = await additionalFormatting(rep, fullyFormatted);
	}

	return fullyFormatted;
}
